type SubjectType = "INDIVIDUAL" | "BUSINESS";

type Application = {
  id: string;
  subjectType: SubjectType;
  status: "DRAFT" | "PENDING_REVIEW" | "CHANGES_REQUIRED" | "APPROVED" | "REJECTED";
  email: string;
  ownerName: string;
  displayName: string;
  countryCode: string;
  emailVerified: boolean;
  providerId?: string;
  version: number;
  reviewNote?: string;
};

type ApplicationProfile = {
  legalName: string;
  dateOfBirth?: string;
  idDocumentType?: string;
  idDocumentNumber?: string;
  registrationNumber?: string;
  registeredAddress: string;
  beneficialOwnerNames?: string[];
  documentRefs: string[];
  settlementNetwork: string;
  settlementWallet: string;
};

type ApplicationDetail = {
  application: Application;
  profile: ApplicationProfile;
  reviews?: Array<{ decision: string; note: string; createdAt: string }>;
};

type OnboardingOptions = {
  apiBase: string;
  showProviderLogin: () => void;
};

const applicationTokenKey = "bizx402-provider-application-token";

function element<T extends HTMLElement>(selector: string): T {
  return document.querySelector<T>(selector)!;
}

function lines(value: string): string[] {
  return value.split(/\r?\n/).map(item => item.trim()).filter(Boolean);
}

function setStatus(id: string, message: string, error = false): void {
  const target = element<HTMLElement>(`#${id}`);
  target.hidden = false;
  target.className = `form-status ${error ? "error" : ""}`;
  target.textContent = message;
}

export function initializeProviderOnboarding(options: OnboardingOptions): void {
  const loginView = element<HTMLElement>("#login-view");
  const appView = element<HTMLElement>("#app-view");
  const registrationView = element<HTMLElement>("#registration-view");
  const registrationForm = element<HTMLFormElement>("#registration-form");
  const applicationLoginForm = element<HTMLFormElement>("#application-login-form");
  let token = sessionStorage.getItem(applicationTokenKey) || "";
  let detail: ApplicationDetail | undefined;

  async function applicationAPI<T>(path: string, init: RequestInit = {}): Promise<T> {
    const headers = new Headers(init.headers);
    headers.set("Accept", "application/json");
    if (init.body) headers.set("Content-Type", "application/json");
    if (token) headers.set("Authorization", `Bearer ${token}`);
    const response = await fetch(`${options.apiBase}${path}`, { ...init, headers });
    const body = await response.json().catch(() => ({})) as { error?: { message?: string } };
    if (!response.ok) throw new Error(body.error?.message || `请求失败：HTTP ${response.status}`);
    return body as T;
  }

  function showRegistration(): void {
    loginView.hidden = true;
    appView.hidden = true;
    registrationView.hidden = false;
  }

  function showProviderLogin(): void {
    registrationView.hidden = true;
    options.showProviderLogin();
  }

  function subjectType(): SubjectType {
    return element<HTMLSelectElement>("#registration-subject-type").value as SubjectType;
  }

  function syncSubjectFields(): void {
    const individual = subjectType() === "INDIVIDUAL";
    element<HTMLElement>("#individual-fields").hidden = !individual;
    element<HTMLElement>("#business-fields").hidden = individual;
  }

  function profilePayload(): ApplicationProfile {
    return {
      legalName: element<HTMLInputElement>("#registration-legal-name").value.trim(),
      dateOfBirth: element<HTMLInputElement>("#registration-date-of-birth").value,
      idDocumentType: element<HTMLInputElement>("#registration-document-type").value.trim(),
      idDocumentNumber: element<HTMLInputElement>("#registration-document-number").value.trim(),
      registrationNumber: element<HTMLInputElement>("#registration-number").value.trim(),
      registeredAddress: element<HTMLInputElement>("#registration-address").value.trim(),
      beneficialOwnerNames: lines(element<HTMLTextAreaElement>("#registration-beneficial-owners").value),
      documentRefs: lines(element<HTMLTextAreaElement>("#registration-document-refs").value),
      settlementNetwork: element<HTMLInputElement>("#registration-network").value.trim(),
      settlementWallet: element<HTMLInputElement>("#registration-wallet").value.trim(),
    };
  }

  function populateForm(current: ApplicationDetail): void {
    const { application, profile } = current;
    element<HTMLSelectElement>("#registration-subject-type").value = application.subjectType;
    element<HTMLInputElement>("#registration-country").value = application.countryCode;
    element<HTMLInputElement>("#registration-email").value = application.email;
    element<HTMLInputElement>("#registration-owner-name").value = application.ownerName;
    element<HTMLInputElement>("#registration-display-name").value = application.displayName;
    element<HTMLInputElement>("#registration-legal-name").value = profile.legalName;
    element<HTMLInputElement>("#registration-address").value = profile.registeredAddress;
    element<HTMLInputElement>("#registration-date-of-birth").value = profile.dateOfBirth || "";
    element<HTMLInputElement>("#registration-document-type").value = profile.idDocumentType || "";
    element<HTMLInputElement>("#registration-document-number").value = profile.idDocumentNumber || "";
    element<HTMLInputElement>("#registration-number").value = profile.registrationNumber || "";
    element<HTMLTextAreaElement>("#registration-beneficial-owners").value = (profile.beneficialOwnerNames || []).join("\n");
    element<HTMLTextAreaElement>("#registration-document-refs").value = profile.documentRefs.join("\n");
    element<HTMLInputElement>("#registration-network").value = profile.settlementNetwork;
    element<HTMLInputElement>("#registration-wallet").value = profile.settlementWallet;
    element<HTMLSelectElement>("#registration-subject-type").disabled = true;
    element<HTMLInputElement>("#registration-email").disabled = true;
    const password = element<HTMLInputElement>("#registration-password");
    password.value = "";
    password.disabled = true;
    password.required = false;
    element("#application-version").textContent = `V${application.version}`;
    element("#save-application").textContent = "保存申请资料 →";
    syncSubjectFields();
  }

  function renderApplication(current: ApplicationDetail): void {
    detail = current;
    populateForm(current);
    const application = current.application;
    const editable = application.status === "DRAFT" || application.status === "CHANGES_REQUIRED";
    registrationForm.querySelectorAll<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>("input, textarea, select")
      .forEach(field => {
        if (!["registration-subject-type", "registration-email", "registration-password"].includes(field.id)) {
          field.disabled = !editable;
        }
      });
    element<HTMLButtonElement>("#save-application").hidden = !editable;
    element<HTMLElement>("#application-state").hidden = false;
    element("#application-state-title").textContent = application.status;
    element("#application-state-copy").textContent = application.reviewNote || stateCopy(application);
    element<HTMLElement>("#verification-actions").hidden = true;
    element<HTMLButtonElement>("#submit-application").hidden = true;
  }

  function stateCopy(application: Application): string {
    if (application.status === "PENDING_REVIEW") return "平台正在审核主体身份与结算钱包，审核期间资料已锁定。";
    if (application.status === "CHANGES_REQUIRED") return "平台要求补充资料，保存后可由平台再次直接审核。";
    return "申请已创建。Demo 暂时跳过邮箱验证和提交步骤，等待平台直接审核。";
  }

  async function loadDetail(): Promise<void> {
    detail = await applicationAPI<ApplicationDetail>("/v1/provider/onboarding");
    renderApplication(detail);
  }

  element("#show-registration").addEventListener("click", showRegistration);
  element("#back-to-provider-login").addEventListener("click", showProviderLogin);
  element<HTMLSelectElement>("#registration-subject-type").addEventListener("change", syncSubjectFields);

  registrationForm.addEventListener("submit", async event => {
    event.preventDefault();
    try {
      if (detail) {
        detail = await applicationAPI<ApplicationDetail>("/v1/provider/onboarding", {
          method: "PUT",
          body: JSON.stringify({
            version: detail.application.version,
            ownerName: element<HTMLInputElement>("#registration-owner-name").value.trim(),
            displayName: element<HTMLInputElement>("#registration-display-name").value.trim(),
            countryCode: element<HTMLInputElement>("#registration-country").value.trim(),
            profile: profilePayload(),
          }),
        });
        renderApplication(detail);
        setStatus("registration-status", "申请资料已加密保存。所有审核动作都会保留版本记录。");
        return;
      }
      const result = await applicationAPI<ApplicationDetail & { token: string; demoVerificationCode?: string }>(
        "/v1/provider/registrations",
        {
          method: "POST",
          body: JSON.stringify({
            subjectType: subjectType(),
            email: element<HTMLInputElement>("#registration-email").value.trim(),
            password: element<HTMLInputElement>("#registration-password").value,
            ownerName: element<HTMLInputElement>("#registration-owner-name").value.trim(),
            displayName: element<HTMLInputElement>("#registration-display-name").value.trim(),
            countryCode: element<HTMLInputElement>("#registration-country").value.trim(),
            profile: profilePayload(),
          }),
        },
      );
      token = result.token;
      sessionStorage.setItem(applicationTokenKey, token);
      renderApplication(result);
      setStatus("registration-status", "申请已创建。Demo 暂时跳过验证，平台可以直接审核。");
    } catch (error) {
      setStatus("registration-status", error instanceof Error ? error.message : "申请保存失败", true);
    }
  });

  applicationLoginForm.addEventListener("submit", async event => {
    event.preventDefault();
    try {
      const result = await applicationAPI<{ token: string }>("/v1/provider/onboarding/login", {
        method: "POST",
        body: JSON.stringify({
          email: element<HTMLInputElement>("#application-login-email").value.trim(),
          password: element<HTMLInputElement>("#application-login-password").value,
        }),
      });
      token = result.token;
      sessionStorage.setItem(applicationTokenKey, token);
      await loadDetail();
      setStatus("application-login-status", "申请已载入。", false);
    } catch (error) {
      setStatus("application-login-status", error instanceof Error ? error.message : "载入失败", true);
    }
  });

  element("#verify-application-email").addEventListener("click", async () => {
    try {
      detail = await applicationAPI<ApplicationDetail>("/v1/provider/onboarding/email/verify", {
        method: "POST",
        body: JSON.stringify({ code: element<HTMLInputElement>("#application-code").value.trim() }),
      });
      renderApplication(detail);
      setStatus("application-action-status", "邮箱验证完成。现在可以提交审核。");
    } catch (error) {
      setStatus("application-action-status", error instanceof Error ? error.message : "验证失败", true);
    }
  });

  element("#resend-application-email").addEventListener("click", async () => {
    try {
      const result = await applicationAPI<{ demoVerificationCode?: string }>(
        "/v1/provider/onboarding/email/resend",
        { method: "POST" },
      );
      if (result.demoVerificationCode) {
        element<HTMLInputElement>("#application-code").value = result.demoVerificationCode;
        setStatus("application-action-status", `Demo 验证码：${result.demoVerificationCode}`);
      } else {
        setStatus("application-action-status", "新验证码已发送，请查收邮箱。");
      }
    } catch (error) {
      setStatus("application-action-status", error instanceof Error ? error.message : "验证码生成失败", true);
    }
  });

  element("#submit-application").addEventListener("click", async () => {
    if (!detail || !window.confirm("确认提交平台审核？提交后资料将锁定。")) return;
    try {
      detail = await applicationAPI<ApplicationDetail>("/v1/provider/onboarding/submit", {
        method: "POST",
        body: JSON.stringify({ version: detail.application.version }),
      });
      renderApplication(detail);
      setStatus("application-action-status", "申请已提交，等待 Platform Admin 审核。");
    } catch (error) {
      setStatus("application-action-status", error instanceof Error ? error.message : "提交失败", true);
    }
  });

  syncSubjectFields();
  if (token) {
    showRegistration();
    void loadDetail().catch(() => {
      token = "";
      sessionStorage.removeItem(applicationTokenKey);
    });
  }
}
